"use client"

import { useState, useEffect, useRef } from "react"
import { Link, useNavigate, useLocation, useParams } from "react-router-dom"
import {
  StarIcon,
  Bars3Icon,
  UserCircleIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/solid"
import { SlidersHorizontal, MapPin, Navigation, Heart, RotateCcw } from "lucide-react"
import LoginPopup from "./login-popup"
import { Autoplay } from 'swiper/modules';
import LogoutConfirmationPopup from "./LogoutConfirmationPopup"
import { ref, onValue, query, orderByChild, set, remove, get } from "../lib/firebase-database"
import { db } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import Footer from "../components/Footer"
import FilterMenu from "../components/filter-menu"
import { MobileBottomNav } from "../components/MobileBottomNav"
import { getCachedLocation, cacheLocation, getCurrentLocation } from "../utils/locationUtils"
import AutoSwipeCarousel from "./ui/auto-swipe-carousel"
import FeaturedOfferCard from "./ui/featured-offer-card"
import { MaterialIcon } from "./MaterialIcon"
import AdCard from "./AdCard"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation as SwiperNavigation, Pagination } from "swiper/modules"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
// import BotpressChat from "./BotpressChat"
import OptimizedImage, { getOptimizedUrl } from "./OptimizedImage"
import AvatarCircle from "./AvatarCircle"
import { HAS_CLERK_PUBLISHABLE_KEY } from "../lib/config"
// Clerk UI components removed � using custom login popup

const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIntersecting] = useState(false)
  const [node, setNode] = useState(null)

  useEffect(() => {
    if (!node) return

    const observer = new IntersectionObserver(([entry]) => {
      setIntersecting(entry.isIntersecting)
    }, options)

    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [node, options])

  return [setNode, isIntersecting]
}

const LazyImage = ({ src, alt, className }) => {
  const [ref, isIntersecting] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: "50px",
  })
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setIsLoaded(false)
  }, [src])

  // Use CDN proxy for resized WebP — same as OptimizedImage
  const optimizedSrc = src ? getOptimizedUrl(src, 480, 300, 75) : "/placeholder.svg"

  return (
    <div ref={ref} className={`relative ${className}`}>
      {isIntersecting ? (
        <>
          <img
            src={optimizedSrc}
            alt={alt}
            className={`${className} ${isLoaded ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}
            onLoad={() => setIsLoaded(true)}
            loading="lazy"
            decoding="async"
          />
          {!isLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-[inherit]" />}
        </>
      ) : (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-[inherit]" />
      )}
    </div>
  )
}

const MAX_RECENTLY_VIEWED = 4

// Recently Viewed Utility Functions
const addToRecentlyViewed = (villaData) => {
  try {
    const existing = getRecentlyViewed()
    const filtered = existing.filter((villa) => villa._id !== villaData._id)

    const updated = [
      {
        ...villaData,
        visitedAt: Date.now(),
      },
      ...filtered,
    ]

    const limited = updated.slice(0, MAX_RECENTLY_VIEWED)
    localStorage.setItem("recentlyViewed", JSON.stringify(limited))

    // Dispatch custom event
    window.dispatchEvent(
      new CustomEvent("recentlyViewedUpdated", {
        detail: limited,
      }),
    )
  } catch (error) {
    console.error("Error adding to recently viewed:", error)
  }
}

const getRecentlyViewed = () => {
  try {
    const stored = localStorage.getItem("recentlyViewed")
    if (!stored) return []

    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed)) return []

    return parsed
      .filter((villa) => villa && villa._id && villa.name && typeof villa.visitedAt === "number")
      .slice(0, MAX_RECENTLY_VIEWED)
  } catch (error) {
    console.error("Error getting recently viewed:", error)
    return []
  }
}

const clearRecentlyViewed = () => {
  try {
    localStorage.removeItem("recentlyViewed")
    window.dispatchEvent(
      new CustomEvent("recentlyViewedUpdated", {
        detail: [],
      }),
    )
  } catch (error) {
    console.error("Error clearing recently viewed:", error)
  }
}

export default function HomePage({ cachedProperties, updateCachedProperties }) {
  const didSkipInitialRouteRefresh = useRef(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [properties, setProperties] = useState([])
  const [allProperties, setAllProperties] = useState([])
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [isMenuClosing, setIsMenuClosing] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [priceRange, setPriceRange] = useState({ min: 0, max: 50000 })
  const [sortBy, setSortBy] = useState("recommended")
  const [userLocation, setUserLocation] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showLoginPopup, setShowLoginPopup] = useState(false)
  const [filters, setFilters] = useState({
    priceRange: [0, 5500],
    guestCount: "all",
    amenities: [],
    showNearby: false,
  })
  const [isBlurred, setIsBlurred] = useState(false)
  const { user, isAdmin, isOwner, signOut } = useAuth()
  const navigate = useNavigate()
  const [showNearbyHomes, setShowNearbyHomes] = useState(false)
  const location = useLocation()
  const [userCity, setUserCity] = useState("Loading location...")
  const [userState, setUserState] = useState("")
  const [isSearchBarSticky, setIsSearchBarSticky] = useState(false)
  const [wishlistedProperties, setWishlistedProperties] = useState({})
  const [promotedVillas, setPromotedVillas] = useState([])
  const [luxuryVillas, setLuxuryVillas] = useState([])
  const [isRefreshingLocation, setIsRefreshingLocation] = useState(false)
  const [locationSource, setLocationSource] = useState("loading")
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false)
  const [featuredOffers, setFeaturedOffers] = useState([])
  const [occasions, setOccasions] = useState([])
  const [currentUserPhoto, setCurrentUserPhoto] = useState(user?.photoURL)
  const [destinations, setDestinations] = useState([])
  const [isDestinationsLoading, setIsDestinationsLoading] = useState(true)
  const [showBot, setShowBot] = useState(false)
  const [aiSearchQuery, setAiSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [placeholderText, setPlaceholderText] = useState("");
  const [recentProperties, setRecentProperties] = useState([])
  const [popularSlideIndex, setPopularSlideIndex] = useState(0)
  const [recentSlideIndex, setRecentSlideIndex] = useState(0)
  const [nearbySlideIndex, setNearbySlideIndex] = useState(0)
  const [luxurySlideIndex, setLuxurySlideIndex] = useState(0)
  const [recentSearches, setRecentSearches] = useState([])
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [filteredRecentSearches, setFilteredRecentSearches] = useState([]);
  const [isSearchTransitioning, setIsSearchTransitioning] = useState(false);
  const MAX_RECENT_SEARCHES = 3;


  // Add this near the other imports at the top of the file
  const facilityOptions = [
    { id: "wifi", label: "WiFi", icon: () => <MaterialIcon name="wifi" /> },
    { id: "tv", label: "TV", icon: () => <MaterialIcon name="tv" /> },
    { id: "ac", label: "AC", icon: () => <MaterialIcon name="ac_unit" /> },
    { id: "kitchen", label: "Kitchen", icon: () => <MaterialIcon name="kitchen" /> },
    { id: "parking", label: "Parking", icon: () => <MaterialIcon name="local_parking" /> },
    { id: "petFriendly", label: "Pet Friendly", icon: () => <MaterialIcon name="pets" /> },
    { id: "pool", label: "Swimming Pool", icon: () => <MaterialIcon name="pool" /> },
    { id: "gym", label: "Gym", icon: () => <MaterialIcon name="fitness_center" /> },
    { id: "gamesRoom", label: "Games Room", icon: () => <MaterialIcon name="sports_esports" /> },
    { id: "garden", label: "Garden Area", icon: () => <MaterialIcon name="yard" /> },
    { id: "mountainView", label: "Mountain View", icon: () => <MaterialIcon name="landscape" /> },
    { id: "beachView", label: "Beach View", icon: () => <MaterialIcon name="beach_access" /> },
    { id: "alcoholAllowed", label: "Alcohol Allowed", icon: () => <MaterialIcon name="local_bar" /> },
    { id: "noDrinking", label: "No Drinking", icon: () => <MaterialIcon name="no_drinks" /> },
    { id: "birthday", label: "Birthday Celebrations", icon: () => <MaterialIcon name="cake" /> },
    { id: "party", label: "Party Celebrations", icon: () => <MaterialIcon name="celebration" /> },
    { id: "indoorGames", label: "Indoor Games", icon: () => <MaterialIcon name="sports_kabaddi" /> },
    { id: "bbq", label: "BBQ", icon: () => <MaterialIcon name="outdoor_grill" /> },
    { id: "meditation", label: "Meditation Area", icon: () => <MaterialIcon name="self_improvement" /> },
    { id: "spa", label: "Spa", icon: () => <MaterialIcon name="spa" /> },
  ].map((facility) => ({
    ...facility,
    icon: () => <div className="text-gray-600">{facility.icon()}</div>,
  }))

  const checkAndShuffleLuxuryVillas = async () => {
    try {
      const shuffleRef = ref(db, "luxuryShuffleData")
      const snapshot = await get(shuffleRef)
      const shuffleData = snapshot.val()

      const today = new Date().toDateString()
      const lastShuffleDate = shuffleData?.lastShuffleDate ? new Date(shuffleData.lastShuffleDate).toDateString() : null

      // If no shuffle today, perform shuffle.
      // Only admins perform write operations to avoid guest/user permission errors.
      if (lastShuffleDate !== today) {
        if (!isAdmin) {
          return
        }

        const luxuryRef = ref(db, "luxuryVillas")
        const luxurySnapshot = await get(luxuryRef)
        const luxuryData = luxurySnapshot.val()

        if (luxuryData) {
          const luxuryIds = Object.keys(luxuryData)

          // Shuffle the array
          const shuffledIds = [...luxuryIds]
          for (let i = shuffledIds.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledIds[i], shuffledIds[j]] = [shuffledIds[j], shuffledIds[i]]
          }

          // Save shuffle data
          const newShuffleData = {
            shuffledOrder: shuffledIds,
            lastShuffleDate: new Date().toISOString(),
            isAutomaticShuffle: true
          }

          await set(shuffleRef, newShuffleData)
          console.log("Daily luxury villa shuffle completed")
        }
      }
    } catch (error) {
      console.error("Error in daily luxury villa shuffle:", error)
    }
  }

  const handleViewAllNearby = () => {
    navigate("/explore", {
      state: {
        filterType: "nearby",
        showNearbyHomes: true,
      },
    })
  }

  const handleViewAllTopRated = () => {
    navigate("/explore", {
      state: {
        filterType: "featuredAndPopular",
        minRating: 4,
        showPromoted: true,
      },
    })
  }

  useEffect(() => {
    fetchProperties()
    console.log("Is Admin:", isAdmin)

    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768
      setIsMobile(newIsMobile)
      if (!newIsMobile) {
        setShowMobileMenu(false)
        document.body.style.overflow = "auto" // Reset overflow on resize to desktop
      }
    }
    window.addEventListener("resize", handleResize)

    // Cleanup on unmount
    return () => {
      window.removeEventListener("resize", handleResize)
      document.body.style.overflow = "auto" // Ensure overflow is reset when leaving the page
    }
  }, [])

  useEffect(() => {
    checkAndShuffleLuxuryVillas()
  }, [isAdmin])

  useEffect(() => {
    if (navigator.geolocation) {
      setUserCity("Loading location...")
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = { lat: position.coords.latitude, lng: position.coords.longitude }
          setUserLocation(newLocation)
          getUserCityName(newLocation.lat, newLocation.lng)
        },
        (error) => {
          console.error("Error fetching location:", error)
          setUserCity("Location not available")
        },
      )
    } else {
      setUserCity("Location not available")
    }
  }, [])

  useEffect(() => {
    if (showNearbyHomes && userLocation) {
      console.log("Applying location filter with user location:", userLocation)
      applyLocationFilter()
    } else if (!showNearbyHomes) {
      console.log("Resetting to all properties")
      setProperties(allProperties)
    }
  }, [showNearbyHomes, userLocation, allProperties])

  useEffect(() => {
    if (!didSkipInitialRouteRefresh.current) {
      didSkipInitialRouteRefresh.current = true
      return
    }

    if (location.pathname === "/") {
      fetchProperties({ silent: true })
    }
  }, [location.pathname])

  useEffect(() => {
    const handleScroll = () => setIsSearchBarSticky(window.scrollY > 180)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (user) {
      const wishlistRef = ref(db, `wishlists/${user.uid}`)
      onValue(wishlistRef, (snapshot) => setWishlistedProperties(snapshot.val() || {}))
    }
  }, [user])

  useEffect(() => {
    const promotedRef = ref(db, "promotedVillas")
    onValue(promotedRef, (snapshot) => {
      const promotedData = snapshot.val()
      if (promotedData) {
        // Use cached/allProperties if available, otherwise do a one-time fetch
        const buildPromotedList = (villasData) => {
          if (!villasData) return []
          return Object.keys(promotedData)
            .map((id) => {
              const villa = villasData[id]
              return villa ? {
                _id: id,
                ...villa,
                position: promotedData[id].position || 0
              } : null
            })
            .filter(Boolean)
            .sort((a, b) => a.position - b.position)
        }

        if (cachedProperties.length > 0) {
          // Build from cache (convert array to object by _id)
          const villasObj = {}
          cachedProperties.forEach(v => { villasObj[v._id] = v })
          setPromotedVillas(buildPromotedList(villasObj))
        } else {
          const villasRef = ref(db, "villas")
          get(villasRef).then(villasSnapshot => {
            setPromotedVillas(buildPromotedList(villasSnapshot.val()))
          }).catch(() => setPromotedVillas([]))
        }
      } else {
        setPromotedVillas([])
      }
    })
  }, [cachedProperties])

  useEffect(() => {
    const fetchLuxuryVillas = async () => {
      try {
        // Get shuffle data + luxury villa IDs in parallel
        const [shuffleSnapshot, luxurySnapshot] = await Promise.all([
          get(ref(db, "luxuryShuffleData")),
          get(ref(db, "luxuryVillas")),
        ])
        const shuffleData = shuffleSnapshot.val()
        const luxuryData = luxurySnapshot.val()
        if (!luxuryData) { setLuxuryVillas([]); return }

        // Get villa details — use cache if available
        let villasData
        if (cachedProperties.length > 0) {
          villasData = {}
          cachedProperties.forEach(v => { villasData[v._id] = v })
        } else {
          const villasSnapshot = await get(ref(db, "villas"))
          villasData = villasSnapshot.val()
        }
        if (!villasData) { setLuxuryVillas([]); return }

        let luxuryList = Object.keys(luxuryData)
          .map((id) => {
            const villa = villasData[id]
            return villa ? { _id: id, ...villa } : null
          })
          .filter(Boolean)

        if (shuffleData?.shuffledOrder) {
          const shuffledOrder = shuffleData.shuffledOrder
          luxuryList = shuffledOrder
            .map(id => luxuryList.find(villa => villa._id === id))
            .filter(Boolean)
            .concat(luxuryList.filter(villa => !shuffledOrder.includes(villa._id)))
        }
        setLuxuryVillas(luxuryList)
      } catch (error) {
        console.error("Error fetching luxury villas:", error)
        setLuxuryVillas([])
      }
    }

    fetchLuxuryVillas()
  }, [cachedProperties])

  useEffect(() => {
    const destinationsRef = ref(db, "destinations")
    const unsubscribe = onValue(destinationsRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const destinationList = Object.entries(data)
          .map(([id, value]) => ({
            id,
            ...value,
          }))
          .filter(dest => dest.isVisible)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
        setDestinations(destinationList)
      } else {
        setDestinations([])
      }
      setIsDestinationsLoading(false)
    }, () => {
      setDestinations([])
      setIsDestinationsLoading(false)
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (user) {
      const notificationsRef = ref(db, `notifications/${user.uid}`)
      return onValue(notificationsRef, (snapshot) => {
        const data = snapshot.val()
        if (data) {
          const hasUnread = Object.values(data).some((notification) => !notification.read)
          setHasUnreadNotifications(hasUnread)
        } else {
          setHasUnreadNotifications(false)
        }
      })
    }
  }, [user])

  useEffect(() => {
    const fetchOccasions = async () => {
      try {
        const occasionsRef = ref(db, "specialOccasions")
        const snapshot = await get(occasionsRef)
        if (snapshot.exists()) {
          const data = snapshot.val()
          const occasionsList = Object.entries(data).map(([id, value]) => ({
            id,
            ...value,
            isVisible: value.isVisible ?? true,
          }))
          setOccasions(occasionsList)
        }
      } catch (error) {
        console.error("Error fetching occasions:", error)
      }
    }

    fetchOccasions()
  }, [])

  useEffect(() => {
    // Fetch featured offers — one-time, no realtime needed
    const fetchFeaturedOffers = async () => {
      try {
        let villasData
        if (cachedProperties.length > 0) {
          villasData = cachedProperties
        } else {
          const villasSnapshot = await get(ref(db, "villas"))
          const data = villasSnapshot.val()
          villasData = data ? Object.entries(data).map(([key, villa]) => ({ _id: key, ...villa })) : []
        }
        const activeOffers = villasData.filter(
          (villa) => villa.isFeaturedOffer && villa.discount > 0 && new Date(villa.offerValidTill) > new Date()
        )
        setFeaturedOffers(activeOffers)
      } catch (error) {
        console.error("Error fetching featured offers:", error)
        setFeaturedOffers([])
      }
    }
    fetchFeaturedOffers()
  }, [cachedProperties])

  const fetchProperties = async ({ silent = false } = {}) => {
    if (cachedProperties.length > 0) {
      const visibleProperties = cachedProperties.filter((property) => !property.isHidden)
      setProperties(visibleProperties)
      setAllProperties(visibleProperties)
      setIsLoading(false)
      return
    }

    if (!silent) setIsLoading(true)
    setError(null)
    const villasRef = ref(db, "villas")
    const villasQuery = query(villasRef, orderByChild("createdAt"))

    try {
      const snapshot = await get(villasQuery)
      const data = snapshot.val()
      const fetchedProperties = data
        ? Object.entries(data)
          .map(([key, value]) => ({
            _id: key,
            ...value,
          }))
          .filter((property) => !property.isHidden)
        : []
      setProperties(fetchedProperties)
      setAllProperties(fetchedProperties)
      updateCachedProperties(fetchedProperties)
    } catch (error) {
      console.error("Error fetching properties:", error)
      setProperties([])
      setAllProperties([])
      setError("Failed to load properties. Please try again later.")
    } finally {
      setIsLoading(false)
    }
  }

  const filterProperties = () => {
    if (searchQuery.length > 0) {
      const searchTerms = searchQuery.toLowerCase().split(" ")
      const filtered = allProperties.filter((property) => {
        const propertyText =
          `${property.name || ""} ${property.mainCity || ""} ${property.address || ""} ${(property.facilities || []).join(" ")}`.toLowerCase()
        return searchTerms.every((term) => propertyText.includes(term))
      })
      setProperties(filtered)
    } else {
      setProperties(allProperties)
    }
  }

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const lat1Num = Number(lat1),
      lng1Num = Number(lng1),
      lat2Num = Number(lat2),
      lng2Num = Number(lng2)
    if (!lat1Num || !lng1Num || !lat2Num || !lng2Num) return Number.POSITIVE_INFINITY
    const R = 6371
    const dLat = (lat2Num - lat1Num) * (Math.PI / 180)
    const dLng = (lng2Num - lng1Num) * (Math.PI / 180)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1Num * (Math.PI / 180)) *
      Math.cos(lat2Num * (Math.PI / 180)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return Number.isFinite(R * c) ? R * c : Number.POSITIVE_INFINITY
  }

  const handleSort = (value) => {
    setSortBy(value)
    const sorted = [...properties]
    switch (value) {
      case "recommended":
        sorted.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
        break
      case "price-low-high":
        sorted.sort((a, b) => (a.rate || 0) - (b.rate || 0))
        break
      case "price-high-low":
        sorted.sort((a, b) => (b.rate || 0) - (a.rate || 0))
        break
      default:
        break
    }
    setProperties(sorted)
  }

  const applyFilters = (newFilters) => {
    let filtered = [...allProperties]
    filtered = filtered.filter((property) => {
      const price = property.rate || 0
      return price >= newFilters.priceRange[0] && price <= newFilters.priceRange[1]
    })
    if (newFilters.guestCount !== "all") {
      const minGuests = Number.parseInt(newFilters.guestCount)
      filtered = filtered.filter((property) => (property.guestCount || 0) >= minGuests)
    }
    if (newFilters.amenities.length > 0) {
      filtered = filtered.filter((property) =>
        newFilters.amenities.every((amenity) => property.facilities && property.facilities.includes(amenity)),
      )
    }
    setProperties(filtered)
    setShowFilters(false)
  }

  const applyLocationFilter = () => {
    if (!userLocation) {
      alert("Please enable location services to see nearby homes")
      setShowNearbyHomes(false)
      return
    }

    const propertiesWithDistance = allProperties
      .map((property) => {
        const propertyLat = property.location?.lat
        const propertyLng = property.location?.lng
        if (!propertyLat || !propertyLng) return { ...property, distance: Number.POSITIVE_INFINITY }
        const distance = calculateDistance(userLocation.lat, userLocation.lng, propertyLat, propertyLng)
        return { ...property, distance }
      })
      .filter((property) => property.distance !== Number.POSITIVE_INFINITY)
      .sort((a, b) => a.distance - b.distance)

    if (propertiesWithDistance.length === 0) {
      setError("No properties found nearby")
      setProperties([])
    } else {
      setError(null)
      setProperties(propertiesWithDistance)
    }
  }

  const toggleMobileMenu = () => {
    if (showMobileMenu) {
      setIsMenuClosing(true)
      setTimeout(() => {
        setShowMobileMenu(false)
        setIsMenuClosing(false)
        setIsBlurred(false)
        document.body.style.overflow = "auto"
      }, 300)
    } else {
      setShowMobileMenu(true)
      setIsBlurred(true)
      document.body.style.overflow = "hidden"
    }
  }

  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true)
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const handleLogoutConfirm = () => {
    signOut()
    setShowLogoutConfirmation(false)
    navigate("/")
  }

  const handleLogoutCancel = () => setShowLogoutConfirmation(false)

  const goToProfile = () => {
    if (isMobile && showMobileMenu) {
      setIsMenuClosing(true)
      setTimeout(() => {
        setShowMobileMenu(false)
        setIsMenuClosing(false)
        setIsBlurred(false)
        document.body.style.overflow = "auto"
        navigate("/profile")
      }, 300)
    } else {
      setShowProfileMenu(false) // Close desktop profile menu if open
      document.body.style.overflow = "auto" // Reset overflow as a precaution
      navigate("/profile")
    }
  }

  const goToAdminDashboard = () => {
    navigate("/admin")
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const toggleLoginPopup = (show) => {
    setShowLoginPopup(show)
    setIsBlurred(show)
    document.body.style.overflow = show ? "hidden" : "auto"
  }

  const getUserCityName = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
      )
      const data = await response.json()
      if (data.results && data.results[0]) {
        const addressComponents = data.results[0].address_components
        let city = "",
          state = ""
        for (const component of addressComponents) {
          if (component.types.includes("locality")) city = component.long_name
          if (component.types.includes("administrative_area_level_1")) state = component.long_name
        }
        setUserCity(city || "Location not available")
        setUserState(state || "")
      }
    } catch (error) {
      console.error("Error fetching location:", error)
      setUserCity("Location not available")
      setUserState("")
    }
  }

  const addToRecentlyViewed = (villaData) => {
    try {
      const existing = getRecentlyViewed();
      const filtered = existing.filter((villa) => villa._id !== villaData._id);
      const updated = [
        {
          ...villaData,
          visitedAt: Date.now(),
        },
        ...filtered,
      ];
      const limited = updated.slice(0, MAX_RECENTLY_VIEWED);
      localStorage.setItem("recentlyViewed", JSON.stringify(limited));
      window.dispatchEvent(
        new CustomEvent("recentlyViewedUpdated", {
          detail: limited,
        }),
      );
      setRecentProperties(limited); // Update state for UI
    } catch (error) {
      console.error("Error adding to recently viewed:", error);
    }
  };

  useEffect(() => {
    const loadRecentlyViewed = () => {
      const recent = getRecentlyViewed();
      setRecentProperties(recent.slice(0, MAX_RECENTLY_VIEWED));
    };

    loadRecentlyViewed();

    // Listen for updates to recently viewed
    const handleRecentlyViewedUpdate = (event) => {
      setRecentProperties(event.detail.slice(0, MAX_RECENTLY_VIEWED));
    };

    window.addEventListener('recentlyViewedUpdated', handleRecentlyViewedUpdate);

    return () => {
      window.removeEventListener('recentlyViewedUpdated', handleRecentlyViewedUpdate);
    };
  }, []);

  const getRecentlyViewed = () => {
    try {
      const stored = localStorage.getItem("recentlyViewed");
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((villa) => villa && villa._id && villa.name && typeof villa.visitedAt === "number")
        .slice(0, MAX_RECENTLY_VIEWED);
    } catch (error) {
      console.error("Error getting recently viewed:", error);
      return [];
    }
  };

  const clearRecentlyViewed = () => {
    try {
      localStorage.removeItem("recentlyViewed");
      window.dispatchEvent(
        new CustomEvent("recentlyViewedUpdated", {
          detail: [],
        }),
      );
      setRecentProperties([]); // Update state for UI
    } catch (error) {
      console.error("Error clearing recently viewed:", error);
    }
  };

  // Recent Searches Utility Functions
  const getRecentSearches = () => {
    try {
      const stored = localStorage.getItem("recentSearches");
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      return parsed.slice(0, MAX_RECENT_SEARCHES);
    } catch (error) {
      console.error("Error getting recent searches:", error);
      return [];
    }
  };

  const clearRecentSearches = () => {
    try {
      localStorage.removeItem("recentSearches");
      setRecentSearches([]);
    } catch (error) {
      console.error("Error clearing recent searches:", error);
    }
  };


  const handleAiSearch = (rawQuery = aiSearchQuery) => {
    const normalizedQuery = `${rawQuery || ""}`.toLowerCase().trim();
    if (!normalizedQuery) {
      setIsSearchTransitioning(true);
      setTimeout(() => {
        navigate("/explore", {
          state: {
            fromHomeSearchTransition: true,
          },
        });
      }, 260);
      return;
    }

    setIsSearching(true);
    setIsSearchTransitioning(true);

    const filters = {};

    // First check for exact destination matches with null checks
    const destinationMatch = destinations.find(
      (dest) =>
        dest &&
        dest.name &&
        (normalizedQuery.includes(dest.name.toLowerCase()) ||
          normalizedQuery.includes(`in ${dest.name.toLowerCase()}`))
    );

    if (destinationMatch) {
      filters.mainCity = destinationMatch.name;
    } else {
      // Try natural language parsing
      const locationRegex = /in\s+([a-zA-Z]+)/;
      const guestRegex = /(\d+)\s+guests?/;
      const budgetRegex = /under\s+₹?(\d+)k?/;

      const locationMatch = normalizedQuery.match(locationRegex);
      const guestMatch = normalizedQuery.match(guestRegex);
      const budgetMatch = normalizedQuery.match(budgetRegex);

      if (locationMatch) {
        filters.mainCity = locationMatch[1];
      } else {
        // Direct city search with safety check
        const cityMatch = normalizedQuery
          .split(" ")
          .find((word) =>
            destinations.some(
              (dest) => dest?.name?.toLowerCase() === word.toLowerCase()
            )
          );

        if (cityMatch) {
          filters.mainCity = cityMatch;
        }
      }

      if (guestMatch) {
        filters.guestCount = Number.parseInt(guestMatch[1], 10);
      }

      if (budgetMatch) {
        const budget = Number.parseInt(budgetMatch[1], 10);
        filters.maxPrice = budget.toString().endsWith("k") ? budget * 1000 : budget;
      }
    }
    // Filter villas with null checks
    const filteredVillas = allProperties.filter((villa) => {
      let passes = true;

      if (filters.mainCity && villa?.mainCity) {
        passes = villa.mainCity.toLowerCase().includes(filters.mainCity.toLowerCase());
      }

      if (filters.guestCount && villa?.guestCount) {
        passes = passes && villa.guestCount >= filters.guestCount;
      }

      if (filters.maxPrice && villa?.rate) {
        passes = passes && villa.rate <= filters.maxPrice;
      }

      // If no filters matched, search by name and description
      if (!filters.mainCity && !filters.guestCount && !filters.maxPrice) {
        const searchTerms = normalizedQuery.split(" ");
        const villaText = `${villa?.name || ""} ${villa?.mainCity || ""} ${villa?.description || ""
          }`.toLowerCase();
        passes = searchTerms.some((term) => villaText.includes(term));
      }

      return passes;
    });

    // Only save search if it produces results
    if (filteredVillas.length > 0) {
      const recentSearches = getRecentSearches();
      const isExistingSearch = recentSearches.includes(normalizedQuery);
      if (!isExistingSearch) {
        const newRecentSearches = [normalizedQuery, ...recentSearches].slice(0, MAX_RECENT_SEARCHES);
        localStorage.setItem("recentSearches", JSON.stringify(newRecentSearches));
        setRecentSearches(newRecentSearches);
        setFilteredRecentSearches(newRecentSearches);
      }
    }

    // Hide search dropdown
    setShowSearchDropdown(false);

    // Navigate to Explore page and let Explore apply query with entry animation
    setTimeout(() => {
      navigate("/explore", {
        state: {
          searchQuery: rawQuery,
          fromHomeSearchTransition: true,
          parsedFilters: filters,
        },
      });
      setIsSearching(false);
    }, 260);
  };

  // Update the handleRecentSearchClick function
  const handleRecentSearchClick = (searchTerm) => {
    setAiSearchQuery(searchTerm);
    setShowSearchDropdown(false);
    handleAiSearch(searchTerm);
  };
  // Add function to remove individual search
  const removeRecentSearch = (searchToRemove) => {
    try {
      const filtered = recentSearches.filter(search => search !== searchToRemove);
      localStorage.setItem("recentSearches", JSON.stringify(filtered));
      setRecentSearches(filtered);
      setFilteredRecentSearches(filtered);
    } catch (error) {
      console.error("Error removing recent search:", error);
    }
  };

  // Add useEffect to filter recent searches on component mount
  useEffect(() => {
    const loadRecentSearches = () => {
      const recent = getRecentSearches();
      // Filter out any searches that might contain "No properties match"
      const filtered = recent.filter(search =>
        !search.toLowerCase().includes("no properties") &&
        !search.toLowerCase().includes("match") &&
        search.trim().length > 0
      );
      setRecentSearches(filtered);
      setFilteredRecentSearches(filtered);
    };
    loadRecentSearches();
  }, []);


  // Load Recent Searches
  useEffect(() => {
    const loadRecentSearches = () => {
      const recent = getRecentSearches();
      setRecentSearches(recent);
    };
    loadRecentSearches();
  }, []);


  const handleNearbyClick = () => {
    if (!userLocation) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const newLocation = { lat: position.coords.latitude, lng: position.coords.longitude }
            setUserLocation(newLocation)
            setShowNearbyHomes(true)
            applyLocationFilter()
          },
          (error) => {
            console.error("Error getting location:", error)
            alert("Please enable location services to see nearby homes")
          },
        )
      }
    } else {
      setShowNearbyHomes(!showNearbyHomes)
      if (!showNearbyHomes) applyLocationFilter()
      else setProperties(allProperties)
    }
  }

  const handleWishlistClick = async (propertyId) => {
    if (!user) {
      toggleLoginPopup(true)
      return
    }

    const wasWishlisted = isPropertyWishlisted(propertyId)
    setWishlistedProperties((prev) => {
      const next = { ...prev }
      if (wasWishlisted) delete next[propertyId]
      else next[propertyId] = true
      return next
    })

    const wishlistRef = ref(db, `wishlists/${user.uid}/${propertyId}`)
    try {
      if (wasWishlisted) await remove(wishlistRef)
      else await set(wishlistRef, true)
    } catch (error) {
      console.error("Wishlist update failed:", error)
      setWishlistedProperties((prev) => {
        const next = { ...prev }
        if (wasWishlisted) next[propertyId] = true
        else delete next[propertyId]
        return next
      })
      alert("Failed to update favorites. Please try again.")
    }
  }

  const isPropertyWishlisted = (propertyId) => !!wishlistedProperties[propertyId]

  const handleLocationSetup = async () => {
    const cachedData = getCachedLocation()
    if (cachedData) {
      setUserLocation(cachedData.location)
      setUserCity(cachedData.city)
      setUserState(cachedData.state)
    } else {
      setUserCity("Loading location...")
    }
  }

  const handleLocationRefresh = async () => {
    setIsRefreshingLocation(true)
    setUserCity("Loading location...")

    try {
      const { location, city, state } = await getCurrentLocation()
      setUserLocation(location)
      setUserCity(city || "Location not available")
      setUserState(state || "")
      cacheLocation(location, city, state)
    } catch (error) {
      console.error("Error updating location:", error)
      setUserCity("Location not available")
    } finally {
      setIsRefreshingLocation(false)
    }
  }

  useEffect(() => {
    handleLocationSetup()
  }, [])

  const handleValueStaysClick = (maxPrice) => {
    navigate("/explore", {
      state: {
        filterType: "valueStays",
        maxPrice: maxPrice,
      },
    })
  }
  useEffect(() => {
    const destinations = ["Lonavala", "Karjat", "Alibag", "Igatpuri", "Panchgani"]
    let currentDestIndex = 0

    setPlaceholderText(`Stays in ${destinations[currentDestIndex]}`)

    const intervalId = setInterval(() => {
      currentDestIndex = (currentDestIndex + 1) % destinations.length
      setPlaceholderText(`Stays in ${destinations[currentDestIndex]}`)
    }, 2400)

    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    if (!user) return

    const userRef = ref(db, `users/${user.uid}`)
    const unsubscribe = onValue(userRef, (snapshot) => {
      const userData = snapshot.val()
      if (userData?.photoURL) {
        setCurrentUserPhoto(userData.photoURL)
      }
    })

    return () => unsubscribe()
  }, [user])

  const villasPerSlide = 4
  const popularVillasForDisplay = [
    ...(promotedVillas || []),
    ...properties
      .filter(
        (property) =>
          (property.averageRating || 0) >= 4 &&
          !promotedVillas.some((pv) => pv._id === property._id) &&
          !featuredOffers.some((fo) => fo._id === property._id),
      )
      .sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0)),
  ]
    .filter((property, index, self) => index === self.findIndex((p) => p._id === property._id))
    .slice(0, 8)

  const recentPropertiesForDisplay = recentProperties.slice(0, MAX_RECENTLY_VIEWED)
  const nearbyPropertiesForDisplay = properties
    .map((property) => ({
      ...property,
      distance:
        userLocation && property.location
          ? calculateDistance(
            userLocation.lat,
            userLocation.lng,
            property.location.lat,
            property.location.lng,
          )
          : Number.POSITIVE_INFINITY,
    }))
    .sort((a, b) => (a.distance || Number.POSITIVE_INFINITY) - (b.distance || Number.POSITIVE_INFINITY))
    .slice(0, 8)

  const luxuryVillasForDisplay = luxuryVillas.slice(0, 8)
  const activeDiscountOffers = (promotedVillas || [])
    .filter(
      (property) =>
        property.discount &&
        property.discount > 0 &&
        new Date(property.offerValidTill || Date.now()) > new Date(),
    )
    .slice(0, 2)
  const hasAnyOffers = featuredOffers.length > 0 || activeDiscountOffers.length > 0

  const popularSlideCount = Math.ceil(popularVillasForDisplay.length / villasPerSlide)
  const popularDesktopMaxIndex = Math.max(popularVillasForDisplay.length + 1 - villasPerSlide, 0)
  const recentDesktopMaxIndex = Math.max(recentPropertiesForDisplay.length - villasPerSlide, 0)
  const nearbyDesktopMaxIndex = Math.max(nearbyPropertiesForDisplay.length + 1 - villasPerSlide, 0)
  const luxuryDesktopMaxIndex = Math.max(luxuryVillasForDisplay.length + 1 - villasPerSlide, 0)

  useEffect(() => {
    const maxPopularIndex = isMobile ? Math.max(popularSlideCount - 1, 0) : popularDesktopMaxIndex
    if (popularSlideIndex > maxPopularIndex) {
      setPopularSlideIndex(maxPopularIndex)
    }
  }, [isMobile, popularDesktopMaxIndex, popularSlideCount, popularSlideIndex])

  useEffect(() => {
    if (recentSlideIndex > recentDesktopMaxIndex) {
      setRecentSlideIndex(recentDesktopMaxIndex)
    }
  }, [recentDesktopMaxIndex, recentSlideIndex])

  useEffect(() => {
    if (nearbySlideIndex > nearbyDesktopMaxIndex) {
      setNearbySlideIndex(nearbyDesktopMaxIndex)
    }
  }, [nearbyDesktopMaxIndex, nearbySlideIndex])

  useEffect(() => {
    if (luxurySlideIndex > luxuryDesktopMaxIndex) {
      setLuxurySlideIndex(luxuryDesktopMaxIndex)
    }
  }, [luxuryDesktopMaxIndex, luxurySlideIndex])

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {/* SEO H1 — visually hidden but crawlable by search engines */}
      <h1 className="sr-only">
        VillaKosh – Book Luxury Villas in Lonavala, Karjat, Alibag, Igatpuri, Panchgani &amp; Neral, Maharashtra
      </h1>
      {isMobile ? (
        <>
          <header className="top-0 left-0 right-0 z-50">
            <div className="relative h-[360px]">
              <Swiper
                modules={[Autoplay, Pagination]}
                autoplay={{ delay: 3500, disableOnInteraction: false }}
                  pagination={false}
                loop={true}
                speed={900} // smoother transition
                className="h-full"
              >
                {[
                  {
                    url: "https://cdn.pixabay.com/photo/2024/04/26/18/36/farm-house-8722429_1280.jpg",
                    text: "Seamlessly Connect with Verified Hosts"
                  },
                  {
                    url: "https://cdn.pixabay.com/photo/2017/09/05/11/03/castle-2717222_1280.jpg",
                    text: "From Cozy Budget Homes to Lavish Retreats"
                  },
                  {
                    url: "https://cdn.pixabay.com/photo/2017/08/27/10/16/interior-2685521_1280.jpg",
                    text: "We Curate Every Stay with Care"
                  }
                ].map((slide, idx) => (
                  <SwiperSlide key={idx}>
                    <div
                      className="absolute inset-0 bg-cover bg-center rounded-b-[32px]"
                      style={{ backgroundImage: `url('${slide.url}')` }} // <-- use url, not image
                    />
                    <div className="absolute inset-0 rounded-b-[32px] bg-gradient-to-b from-black/80 via-black/60 to-transparent"></div>
                    <div className="absolute inset-0 flex flex-col justify-center items-center px-4">
                      <p className="text-[24px] text-white text-center font-semibold drop-shadow">{slide.text}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className="absolute top-0 left-0 right-0 z-10 px-4 py-4 flex justify-between items-center"
                style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.5) 90%, rgba(0,0,0,0.0.8) 100%)" }}>
                {/* Left: Notification bell */}
                <button onClick={() => {}} className="p-2">
                  <div className="relative">
                    {hasUnreadNotifications && (
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                    )}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                  </div>
                </button>

                {/* Center: Logo */}
                <span className="text-2xl font-bold text-white drop-shadow" aria-hidden="true">VillaKosh</span>

                {/* Right: Sign in or Avatar */}
                {user ? (
                  <div className="flex items-center gap-2">
                    <button onClick={goToProfile}>
                      <AvatarCircle
                        src={currentUserPhoto}
                        name={user?.displayName || user?.email}
                        className="h-8 w-8 border-2 border-white"
                        textClassName="text-xs"
                      />
                    </button>
                    
                  </div>
                ) : (
                  <div className="flex items-center">
                    {HAS_CLERK_PUBLISHABLE_KEY ? (
                      <button className="rounded-full border border-white/40 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white hover:text-black" onClick={() => toggleLoginPopup(true)}>Sign in</button>
                    ) : (
                      <Link
                        to="/login"
                        className="rounded-full border border-white/40 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white hover:text-black"
                      >
                        Sign in
                      </Link>
                    )}
                  </div>
                )}
              </div>
              <div className="absolute left-0 right-0 bottom-0 z-20 px-8 pb-4">
                <div className="flex items-center gap-2 text-white mb-2">
                  <MapPin className="h-5 w-5" />
                  <div className="flex-1">
                    <div className="text-sm opacity-60">Current Location</div>
                    <div className="text-sm font-semibold flex items-center gap-2">
                      {userCity}
                      {userState && <>, {userState}</>}
                      <button
                        onClick={handleLocationRefresh}
                        disabled={isRefreshingLocation}
                        className="p-1 hover:bg-white/10 rounded-full transition-colors"
                      >
                        <RotateCcw className={`h-4 w-4 ${isRefreshingLocation ? "animate-spin" : ""}`} />
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  className={`relative transition-all duration-300 ${
                    isSearchTransitioning ? "-translate-y-1 scale-[1.02] opacity-90" : ""
                  }`}
                >
                  <input
                    type="text"
                    placeholder={placeholderText}
                    value={aiSearchQuery}
                    onChange={(e) => setAiSearchQuery(e.target.value)}
                    onFocus={() => {
                      if (filteredRecentSearches.length > 0) {
                        setShowSearchDropdown(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding to allow click on dropdown items
                      setTimeout(() => setShowSearchDropdown(false), 200);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAiSearch();
                      }
                      if (e.key === 'Escape') {
                        setShowSearchDropdown(false);
                      }
                    }}
                    className="w-full py-3 px-4 pr-12 rounded-full bg-white text-gray-900 placeholder-gray-500 focus:outline-none text-sm"
                  />
                  <button
                    className="absolute right-5 top-1/2 -translate-y-1/2"
                    onClick={handleAiSearch}
                    disabled={isSearching || isSearchTransitioning}
                  >
                    {isSearching ? (
                      <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full" />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  </button>

                  {/* Recent searches dropdown below search bar */}
                  {showSearchDropdown && filteredRecentSearches.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex justify-between items-center">
                          <h3 className="text-sm font-semibold text-gray-700">Recent Searches</h3>
                          <button
                            onClick={() => setShowSearchDropdown(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="p-3 space-y-2">
                        {filteredRecentSearches.slice(0, MAX_RECENT_SEARCHES).map((search, index) => (
                          <div
                            key={`${search}-${index}`}
                            className="flex items-center justify-between px-3 py-2.5 bg-[#f6f7f8] hover:bg-[#eef0f2] rounded-xl transition-colors"
                          >
                            <button
                              onClick={() => handleRecentSearchClick(search)}
                              className="flex items-center gap-2.5 flex-1 text-left min-w-0"
                            >
                              <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              <span className="text-sm text-gray-700 truncate">{search}</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeRecentSearch(search);
                              }}
                              className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                              aria-label="Remove recent search"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>


          <main className="mt-[30px] bg-[#EDEFEF] min-h-screen pb-16">
            <div className="bg-[#EDEFEF] rounded-t-[32px] -mt-6 px-4 py-1 relative z-10">
              {/* Destinations Section */}
              <div className="mb-6 mt-4">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-[21px] font-semibold">Destinations</h2>
                  {isAdmin && (
                    <Link to="/admin/destinations" className="text-red-500 text-sm font-medium">
                      Manage
                    </Link>
                  )}
                </div>
                <Swiper
                  modules={[Pagination]}
                  spaceBetween={8}
                  slidesPerView={3.5}
                  pagination={{
                    clickable: true,
                  }}
                  className="destinations-swiper"
                >
                  {isDestinationsLoading
                    ? Array.from({ length: 6 }).map((_, index) => (
                        <SwiperSlide key={`destination-skeleton-${index}`}>
                          <div className="flex flex-col items-center">
                            <div className="w-20 h-20 rounded-full bg-gray-200 border-2 border-gray-200 animate-pulse" />
                            <div className="mt-3 h-4 w-16 bg-gray-200 rounded animate-pulse" />
                            <div className="mt-2 h-3 w-14 bg-gray-200 rounded animate-pulse" />
                          </div>
                        </SwiperSlide>
                      ))
                    : destinations
                        .filter(dest => dest && dest.isVisible && dest.name && typeof dest.name === 'string') // Add stricter type check
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((destination) => (
                          <SwiperSlide key={destination.id}>
                            <Link
                              to={`/villas-in-${destination.name.toLowerCase()}`}
                              className="flex flex-col items-center group"
                            >
                              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200 transition-transform group-hover:scale-105">
                                <OptimizedImage
                                  src={destination.imageUrl || "/placeholder.jpg"}
                                  alt={`Villas in ${destination.name || 'Unknown Location'}`}
                                  className="w-full h-full"
                                  preset="avatar"
                                />
                              </div>
                              <span className="mt-3 text-sm font-medium text-gray-900">
                                {destination.name || 'Unknown Location'}
                              </span>
                              {destination.propertyCount > 0 && (
                                <span className="text-sm text-gray-500">
                                  {destination.propertyCount} properties
                                </span>
                              )}
                            </Link>
                          </SwiperSlide>
                        ))}
                </Swiper>
              </div>

              {/* Recently Visited Properties Section */}
              {recentProperties.length > 0 && (
                <div className="mb-6 mt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h2 className="text-[21px] font-semibold">Recently Viewed Properties</h2>
                  </div>
                  <div className="overflow-x-auto -mx-4 scrollbar-hide">
                    <div className="flex gap-3 px-4 pb-4">
                      {recentPropertiesForDisplay.map((property, index) => (
                        <Link
                          to={`/villa/${property._id}`}
                          key={`${property._id}-${index}`}
                          className="flex-shrink-0 w-[320px] bg-gray-50 rounded-[20px] overflow-hidden border-2 shadow border-gray-200"
                          onClick={() => addToRecentlyViewed(property)}
                        >
                          <div className="relative p-2">
                            <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                              Recent
                            </div>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleWishlistClick(property._id);
                              }}
                              className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                            >
                              {isPropertyWishlisted(property._id) ? (
                                <Heart className="h-5 w-5 text-red-500 fill-current" />
                              ) : (
                                <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                              )}
                            </button>
                            <div className="rounded-[10px] overflow-hidden h-40">
                              <OptimizedImage
                                src={property.thumbnail || property.images?.[0] || "/placeholder.svg"}
                                alt={property.name}
                                className="w-full h-full"
                                preset="cardSmall"
                              />
                            </div>
                          </div>
                          <div className="px-3 pb-2">
                            <h3 className="text-base font-semibold text-gray-900 line-clamp-1">{property.name}</h3>
                            <div className="mt-1 flex items-center justify-between text-sm text-gray-600">
                              <div className="flex items-center">
                                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="ml-1 truncate">{property.mainCity}</span>
                              </div>
                              <div className="flex items-center">
                                <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                                <span className="ml-1 font-medium">
                                  {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                                </span>
                                <span className="ml-1">({property.reviewCount || 0})</span>
                              </div>
                            </div>
                            <div className="mt-1 flex items-center justify-between">
                              <p className="text-base font-bold text-gray-900">
                                ₹{property.rate}
                                {property.pricingType === 'perPerson' && property.rate && (property.perPersonGuestCount || property.guestCount) && (
                                  <span className="text-xs text-gray-600 ml-1">
                                    Est.₹{Math.round(property.rate / (property.perPersonGuestCount || property.guestCount))}/guest
                                  </span>
                                )}
                              </p>
                              <div className="text-sm text-gray-600 truncate">
                                {userLocation && property.location ? (
                                  <span>{Math.round(calculateDistance(userLocation.lat, userLocation.lng, property.location.lat, property.location.lng) * 10) / 10} km</span>
                                ) : (
                                  <span>N/A</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {/* Value Stays Section */}
              <div className="mb-6 mt-4">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-[21px] font-semibold">Value Stays</h2>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    onClick={() => handleValueStaysClick(1000)}
                    className="relative rounded-2xl overflow-hidden h-[248px] cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    <img
                      src="https://cdn.pixabay.com/photo/2020/10/18/09/16/bedroom-5664221_1280.jpg"
                      alt="Hotel room"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 p-4 text-white">
                      <h3 className="text-2xl font-bold mb-2">Stays</h3>
                      <div className="bg-red-400 text-white px-2 py-1 rounded-full inline-block text-lg">
                        Under ₹999
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div
                      onClick={() => handleValueStaysClick(2000)}
                      className="relative rounded-2xl overflow-hidden h-[120px] cursor-pointer hover:opacity-90 transition-opacity"
                    >
                      <img
                        src="https://cdn.pixabay.com/photo/2019/08/19/13/58/bed-4416515_1280.jpg"
                        alt="Hostel room"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 p-4 text-white">
                        <h3 className="text-2xl font-bold mb-1">Stays</h3>
                        <div className="bg-red-400 text-white px-3 py-1 rounded-full inline-block">Under ₹1999</div>
                      </div>
                    </div>
                    <div
                      onClick={() => handleValueStaysClick(3000)}
                      className="relative rounded-2xl overflow-hidden h-[120px] cursor-pointer hover:opacity-90 transition-opacity"
                    >
                      <img
                        src="https://cdn.pixabay.com/photo/2016/11/18/22/21/architecture-1837150_1280.jpg"
                        alt="Homestay"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 p-4 text-white">
                        <h3 className="text-2xl font-bold mb-1">Stays</h3>
                        <div className="bg-red-400 text-white px-3 py-1 rounded-full inline-block">Under ₹2999</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Marquee News Bar - Mobile */}
              <div className="marquee-container">
                <span className="marquee-text">
                  Note: Pricing varies for each Stay. Some properties follow a per-person rate, while others have fixed rates for the entire stay. Additionally, some stays offer different packages with or without food. Please check individual listings for exact pricing and inclusions
                </span>
              </div>


              {/* Popular Villas Section */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-[21px] font-semibold">Popular Villa's</h2>
                  <button onClick={handleViewAllTopRated} className="text-red-500 text-sm font-medium hover:text-red-600 transition-colors">
                    Explore More
                  </button>
                </div>
                <div className="overflow-x-auto -mx-4 scrollbar-hide">
                  <div className="flex gap-3 px-4 pb-4">
                    {popularVillasForDisplay.map((property) => (
                      <Link
                        to={`/villa/${property._id}`}
                        key={property._id}
                        className="flex-shrink-0 w-[320px] bg-gray-50 rounded-[20px] overflow-hidden border-2 shadow border-gray-200"
                        onClick={() => addToRecentlyViewed(property)}
                      >
                        <div className="relative p-2">
                          {promotedVillas.some((pv) => pv._id === property._id) ? (
                            <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-purple-600 to-blue-500 text-white px-2 py-0.5 rounded-full text-xs font-medium shadow-sm">
                              Featured
                            </div>
                          ) : property.averageRating >= 4 ? (
                            <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-orange-500 to-red-500 text-white px-2 py-0.5 rounded-full text-xs font-medium shadow-sm">
                              Popular
                            </div>
                          ) : null}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleWishlistClick(property._id);
                            }}
                            className="absolute top-3 right-3 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-105 transition-transform duration-200"
                          >
                            <Heart className={`h-5 w-5 ${isPropertyWishlisted(property._id) ? 'text-red-500 fill-current' : 'text-gray-500 hover:text-red-500'}`} />
                          </button>
                          <div className="rounded-[10px] overflow-hidden">
                            <div className="grid grid-cols-2 gap-1 h-40">
                              <div className="relative h-40">
                                <LazyImage
                                  src={property.thumbnail || property.images[0]}
                                  alt={property.name}
                                  className="w-full h-full object-cover rounded-l-[20px]"
                                />
                              </div>
                              <div className="grid grid-rows-2 gap-2 h-40">
                                <div className="h-[5rem]">
                                  <LazyImage
                                    src={property.images[1] || "/placeholder.svg"}
                                    alt={property.name}
                                    className="w-full h-full object-cover rounded-tr-[15px]"
                                  />
                                </div>
                                <div className="h-[5rem]">
                                  <LazyImage
                                    src={property.images[2] || "/placeholder.svg"}
                                    alt={property.name}
                                    className="w-full h-full object-cover rounded-br-[15px]"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="px-3 pb-2">
                          <h3 className="text-base font-semibold text-gray-900 line-clamp-1">{property.name}</h3>
                          <div className="mt-1 flex items-center justify-between text-sm text-gray-600">
                            <div className="flex items-center">
                              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="ml-1 truncate">{property.mainCity}</span>
                            </div>
                            <div className="flex items-center">
                              <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                              <span className="ml-1 font-medium">
                                {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                              </span>
                              <span className="ml-1">({property.reviewCount || 0})</span>
                            </div>
                          </div>
                          <div className="mt-1 flex items-center justify-between">
                            <p className="text-base font-bold text-gray-900">
                              ₹{property.rate}
                              {property.pricingType === 'perPerson' && property.rate && (property.perPersonGuestCount || property.guestCount) && (
                                <span className="text-xs text-gray-600 ml-1">
                                  Est.₹{Math.round(property.rate / (property.perPersonGuestCount || property.guestCount))}/guest
                                </span>
                              )}
                            </p>
                            <div className="text-sm text-gray-600 truncate">
                              {userLocation && property.location ? (
                                <span>{Math.round(calculateDistance(userLocation.lat, userLocation.lng, property.location.lat, property.location.lng) * 10) / 10} km</span>
                              ) : (
                                <span>N/A</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              {/*<div className="mb-6 w-full">
                <AdCard
                  adSlot="5292128191"
                  className="w-full h-[100px] md:h-[120px] max-h-[100px] md:max-h-[120px] box-border border-2 border-gray-300 mx-auto rounded-xl transition-all duration-300"
                />
              </div>*/}
              <div className="mt-[10px]">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-[21px] font-semibold">Properties Near You</h2>
                  <button onClick={handleViewAllNearby} className="text-red-500 text-sm font-medium">
                    View All
                  </button>
                </div>
                <div className="overflow-x-auto -mx-4 scrollbar-hide">
                  <div className="flex gap-2 px-4 pb-4">
                    {properties
                      .map((property) => ({
                        ...property,
                        distance:
                          userLocation && property.location
                            ? calculateDistance(
                              userLocation.lat,
                              userLocation.lng,
                              property.location.lat,
                              property.location.lng,
                            )
                            : Number.POSITIVE_INFINITY,
                      }))
                      .sort(
                        (a, b) => (a.distance || Number.POSITIVE_INFINITY) - (b.distance || Number.POSITIVE_INFINITY),
                      )
                      .map((property) => (
                        <Link
                          to={`/villa/${property._id}`}
                          key={property._id}
                          className="flex-shrink-0 w-[180px] bg-white rounded-[20px] overflow-hidden relative h-[240px]"
                          onClick={() => addToRecentlyViewed(property)}
                        >
                          <div className="relative w-full h-full">
                            <OptimizedImage
                              src={property.thumbnail || property.images[0]}
                              alt={property.name}
                              className="w-full h-full"
                              preset="cardSmall"
                            />
                            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/70 to-transparent" />
                            <button
                              onClick={(e) => {
                                e.preventDefault()
                                handleWishlistClick(property._id)
                              }}
                              className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                            >
                              {isPropertyWishlisted(property._id) ? (
                                <Heart className="h-5 w-5 text-red-500 fill-current" />
                              ) : (
                                <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                              )}
                            </button>

                            <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                              <h3 className="text-base font-bold line-clamp-2 mb-2">{property.name}</h3>
                              <div className="flex items-center opacity-100 mb-2">
                                {userLocation && property.location ? (
                                  <span className="text-sm truncate">
                                    {Math.round(
                                      calculateDistance(
                                        userLocation.lat,
                                        userLocation.lng,
                                        property.location.lat,
                                        property.location.lng,
                                      ) * 10,
                                    ) / 10}{" "}
                                    km away
                                  </span>
                                ) : (
                                  <span className="text-sm">Distance N/A</span>
                                )}
                              </div>
                              <div className="flex items-center justify-between">
                                <p className="text-base font-bold whitespace-nowrap">From ₹{property.rate}</p>
                                <div className="flex items-center ml-2 flex-shrink-0">
                                  <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                                  <span className="ml-1">
                                    {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                  </div>
                </div>
              </div>
              {/*<div className="mb-6 w-full">
                <AdCard
                  adSlot="6596506975"
                  className="w-full h-[100px] md:h-[120px] max-h-[100px] md:max-h-[120px] box-border border-2 border-gray-300 mx-auto rounded-xl transition-all duration-300"
                />
              </div>*/}
              {featuredOffers.length > 0 && (
              <div className="mt-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-[21px] font-semibold">Offers & Discounts</h2>
                  <button onClick={() => navigate("/offers")} className="text-red-500 text-sm font-medium">
                    View All
                  </button>
                </div>
                <div className="overflow-x-auto scrollbar-hide">
                  <div className="flex gap-3">
                      <div className="flex-shrink-0 w-full min-w-[280px] bg-white rounded-xl overflow-hidden relative h-[248px]">
                        <div className="relative w-full h-full">
                          <Swiper
                            modules={[Autoplay, Pagination]}
                            autoplay={{
                              delay: 3000,
                              disableOnInteraction: false,
                              pauseOnMouseEnter: true,
                            }}
                            speed={1000}
                            loop={true}
                            pagination={{
                              clickable: true,
                              renderBullet: function (index, className) {
                                return `
                    <span class="${className} relative w-2 h-2 bg-white/50 rounded-full overflow-hidden">
                      <span class="absolute inset-0 bg-white animate-progress"></span>
                    </span>
                  `
                              },
                            }}
                            className="h-full offers-swiper"
                          >
                            {featuredOffers.map((property) => (
                              <SwiperSlide key={property._id}>
                                <Link to={`/villa/${property._id}`} className="block relative h-full">
                                  <OptimizedImage
                                    src={property.thumbnail || property.images[0]}
                                    alt={property.name}
                                    preset="cardSmall"
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-black/40" />
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                                  >
                                  </button>
                                  <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                                    <h3 className="text-lg font-bold line-clamp-2 mb-2">
                                      {property.offerMessage ||
                                        `${property.name} offers you ${property.discount}% discount on Weekdays`}
                                    </h3>
                                    <div className="mt-2">
                                      <div className="flex items-center gap-2 mb-2">
                                        <p className="text-[15px] line-through text-gray-300">₹{property.rate}</p>
                                        <p className="text-[22px] font-bold">
                                          From ₹{Math.round(property.rate * (1 - property.discount / 100))}
                                        </p>
                                      </div>
                                      <div className="flex justify-between items-center text-xs text-gray-300 mb-2">
                                        <span>*T&C Apply</span>
                                        <span>
                                          Valid till {new Date(property.offerValidTill || Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </Link>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </div>
                      </div>
                  </div>
                </div>
              </div>
              )}
              {/*<div className="mb-6 w-full">
                <AdCard
                  adSlot="1240964864"
                  className="w-full h-[100px] md:h/[120px] max-h-[100px] md:max-h-[120px] box-border border-2 border-gray-300 mx-auto rounded-xl transition-all duration-300"
                />
              </div> */}
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-[21px] font-semibold">Luxury Home's</h2>
                  <button
                    onClick={() =>
                      navigate("/explore", {
                        state: { filterType: "luxury" },
                      })
                    }
                    className="text-red-500 text-sm font-medium"
                  >
                    View All
                  </button>
                </div>
                <div className="overflow-x-auto -mx-4 scrollbar-hide">
                  <div className="flex gap-2 px-4 pb-4">
                    {luxuryVillas
                      .map((property) => (
                        <Link
                          to={`/villa/${property._id}`}
                          key={property._id}
                          className="flex-shrink-0 w-[320px] bg-gray-50 rounded-[20px] overflow-hidden border-2 shadow border-gray-200"
                          onClick={() => addToRecentlyViewed(property)}
                        >
                          <div className="relative p-2">
                            <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-yellow-600 to-amber-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                              Luxury
                            </div>
                            <button
                              onClick={(e) => {
                                e.preventDefault()
                                handleWishlistClick(property._id)
                              }}
                              className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                            >
                              {isPropertyWishlisted(property._id) ? (
                                <Heart className="h-5 w-5 text-red-500 fill-current" />
                              ) : (
                                <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                              )}
                            </button>
                            <div className="rounded-[10px] overflow-hidden h-40">
                              <Swiper
                                modules={[SwiperNavigation, Pagination]}
                                spaceBetween={0}
                                slidesPerView={1}
                                pagination={{ clickable: true }}
                                className="h-full w-full"
                              >
                                {[
                                  property.thumbnail || property.images[0],
                                  property.images[1] || "/placeholder.svg",
                                  property.images[2] || "/placeholder.svg",
                                  property.images[3] || "/placeholder.svg",
                                ]
                                  .filter(Boolean)
                                  .slice(0, 4)
                                  .map((image, index) => (
                                    <SwiperSlide key={index}>
                                      <OptimizedImage
                                        src={image || "/placeholder.svg"}
                                        alt={`${property.name} - Image ${index + 1}`}
                                        className="w-full h-full"
                                        preset="cardSmall"
                                      />
                                    </SwiperSlide>
                                  ))}
                              </Swiper>
                            </div>
                          </div>

                          <div className="px-3 pb-2">
                            <h3 className="text-base font-semibold text-gray-900 line-clamp-1">{property.name}</h3>
                            <div className="mt-1 flex items-center justify-between text-sm text-gray-600">
                              <div className="flex items-center">
                                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="ml-1 truncate">{property.mainCity}</span>
                              </div>
                              <div className="flex items-center">
                                <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                                <span className="ml-1 font-medium">
                                  {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                                </span>
                                <span className="ml-1">({property.reviewCount || 0})</span>
                              </div>
                            </div>
                            <div className="mt-1 flex items-center justify-between">
                              <p className="text-base font-bold text-gray-900">
                                ₹{property.rate}
                                {property.pricingType === 'perPerson' && property.rate && (property.perPersonGuestCount || property.guestCount) && (
                                  <span className="text-xs text-gray-600 ml-1">
                                    Est.₹{Math.round(property.rate / (property.perPersonGuestCount || property.guestCount))}/guest
                                  </span>
                                )}
                              </p>
                              <div className="text-sm text-gray-600 truncate">
                                {userLocation && property.location ? (
                                  <span>{Math.round(calculateDistance(userLocation.lat, userLocation.lng, property.location.lat, property.location.lng) * 10) / 10} km</span>
                                ) : (
                                  <span>N/A</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                  </div>
                </div>
              </div>
              {/*<div className="mb-6 w-full">
                <AdCard
                  adSlot="4947016404"
                  className="w-full h-[100px] md:h/[120px] max-h-[100px] md:max-h-[120px] box-border border-2 border-gray-300 mx-auto rounded-xl transition-all duration-300"
                />
              </div>*/}
              <div className="mt-6">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-[21px] font-semibold">Celebrate Your Special Occasion</h2>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {occasions
                    .filter((occasion) => occasion.isVisible)
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((occasion) => (
                      <Link
                        key={occasion.id}
                        to="/explore"
                        state={{
                          filterType: "occasion",
                          facilities: occasion.facilities,
                          occasionTitle: occasion.title,
                        }}
                        className="relative rounded-xl overflow-hidden h-[120px] cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        <OptimizedImage
                          src={occasion.imageUrl || "/placeholder.svg"}
                          alt={occasion.title}
                          preset="cardSmall"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 p-3 text-white">
                          <h3 className="text-lg font-bold">{occasion.title}</h3>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
              {/* List Your Home Section - Mobile */}
              <div className="mt-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-[21px] font-semibold">For Property Owners</h2>
                </div>
                <Link to="/for-owner" className="block">
                  <div className="relative rounded-xl overflow-hidden h-[220px] cursor-pointer hover:opacity-90 transition-opacity">
                    <img
                      src="https://cdn.pixabay.com/photo/2020/04/28/04/03/villa-5102544_1280.jpg"
                      alt="List your property"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-black/40"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="text-lg font-bold mb-1">
                        List Your Property on VillaKosh
                      </h3>
                      <p className="text-sm text-white mb-2">
                        Direct WhatsApp inquiries from guests – no middleman. Promote your villa to thousands of travelers.
                      </p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">Direct Contact</span>
                        <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">Free Listing(Limited-Time)</span>
                        <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">Verified Guests</span>
                      </div>
                      <button className="bg-red-500 text-white hover:bg-red-600 px-4 py-1.5 rounded-full text-sm font-medium transition-colors">
                        List Now
                      </button>
                    </div>
                  </div>
                </Link>
              </div>

              <div className="mt-4 mb-6 w-full">
                <AdCard
                  adSlot="4925059001"
                  className="w-full h-[100px] sm:h-[110px] md:h-[248px] lg:h-[248px] mx-auto rounded-xl transition-all duration-300"
                />
              </div>
            </div>
          </main>
        </>
      ) : (
        <>
          <header className="bg-black text-white p-4 animate-fadeIn z-50">
            <div className="container mx-auto px-4 lg:px-8">
              <div className="flex justify-between items-center">
                {isMobile ? (
                  <>
                    <button onClick={() => {}} className="p-1">
                      <div className="relative">
                        {hasUnreadNotifications && (
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                        )}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                      </div>
                    </button>
                    <h1 className="text-2xl font-bold">VillaKosh</h1>
                    {user ? (
                      <button onClick={goToProfile}>
                        <AvatarCircle
                          src={currentUserPhoto}
                          name={user?.displayName || user?.email}
                          className="h-8 w-8"
                          textClassName="text-xs"
                        />
                      </button>
                    ) : (
                      <div className="flex items-center">
                        {HAS_CLERK_PUBLISHABLE_KEY ? (
                          <button className="rounded-full border border-white/40 px-2.5 py-1 text-[11px] font-medium text-white transition hover:bg-white hover:text-black" onClick={() => toggleLoginPopup(true)}>Sign in</button>
                        ) : (
                          <Link
                            to="/login"
                            className="rounded-full border border-white/40 px-2.5 py-1 text-[11px] font-medium text-white transition hover:bg-white hover:text-black"
                          >
                            Sign in
                          </Link>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <h1 className="text-2xl font-bold">VillaKosh</h1>
                    <nav className="flex items-center space-x-4">
                      <Link
                        to="/for-owner"
                        className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                      >
                        For Owners
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                        >
                          Admin Dashboard
                        </Link>
                      )}
                      {isOwner && (
                        <Link
                          to="/owner"
                          className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                        >
                          Your Villa
                        </Link>
                      )}
                      <Link
                        to="/wishlists"
                        className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                      >
                        Wishlists
                      </Link>
                      {user ? (
                        <div className="flex items-center gap-3">
                          
                          <div className="relative">
                          <button
                            onClick={() => setShowProfileMenu(!showProfileMenu)}
                            className="flex items-center space-x-2 hover:bg-gray-700 px-3 py-2 rounded transition duration-300"
                          >
                            <AvatarCircle
                              src={currentUserPhoto}
                              name={user?.displayName || user?.email}
                              className="w-8 h-8"
                              textClassName="text-xs"
                            />
                            <ChevronDownIcon className="h-4 w-4" />
                          </button>
                          {showProfileMenu && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 text-gray-700 z-50">
                              <div className="px-4 py-2 border-b">
                                <p className="text-sm font-medium">Hi, {user.displayName}</p>
                              </div>
                              <button
                                onClick={goToProfile}
                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                              >
                                View Profile
                              </button>
                              <button
                                onClick={() => navigate("/bookings")}
                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                              >
                                Your Bookings
                              </button>
                              {isOwner && (
                                <Link
                                  to="/owner"
                                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                  onClick={() => setShowProfileMenu(false)}
                                >
                                  Owner Dashboard
                                </Link>
                              )}
                              {isAdmin && (
                                <button
                                  onClick={goToAdminDashboard}
                                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                >
                                  Admin Dashboard
                                </button>
                              )}
                              <button
                                onClick={handleLogoutClick}
                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600"
                              >
                                Logout
                              </button>
                            </div>
                          )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {HAS_CLERK_PUBLISHABLE_KEY ? (
                            <button className="rounded-full border border-white/30 px-4 py-2 text-sm font-medium transition hover:bg-white hover:text-black" onClick={() => toggleLoginPopup(true)}>Sign in</button>
                          ) : (
                            <Link
                              to="/login"
                              className="rounded-full border border-white/30 px-4 py-2 text-sm font-medium transition hover:bg-white hover:text-black"
                            >
                              Sign in
                            </Link>
                          )}
                          
                        </div>
                      )}
                    </nav>
                  </>
                )}
              </div>
            </div>
          </header>

          {isMobile && showMobileMenu && (
            <div
              className={`fixed inset-0 z-50 ${isMenuClosing ? "mobile-menu-overlay-exit" : "mobile-menu-overlay-enter"}`}
            >
              <div
                className={`bg-white w-64 h-full shadow-lg overflow-y-auto ${isMenuClosing ? "animate-slideOutLeft" : "animate-slideInLeft"}`}
              >
                <div className="p-4 flex bg-black justify-between items-center border-b">
                  <h2 className="text-xl text-white font-bold">Menu</h2>
                  <XMarkIcon className="h-6 w-6 cursor-pointer text-white" onClick={toggleMobileMenu} />
                </div>
                <nav className="p-4">
                  <Link
                    to="/for-owner"
                    className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                    onClick={toggleMobileMenu}
                  >
                    For House Owners
                  </Link>
                  <Link
                    to="/wishlists"
                    className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                    onClick={toggleMobileMenu}
                  >
                    Wishlists
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                      onClick={toggleMobileMenu}
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  {user && (
                    <button
                      onClick={goToProfile}
                      className="block w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                    >
                      Profile
                    </button>
                  )}
                  {user && isOwner && (
                    <Link
                      to="/owner"
                      className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                      onClick={toggleMobileMenu}
                    >
                      Owner Dashboard
                    </Link>
                  )}
                  {user ? (
                    <button
                      onClick={handleLogoutClick}
                      className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                    >
                      Logout
                    </button>
                  ) : (
                    <div className="space-y-2">
                      {HAS_CLERK_PUBLISHABLE_KEY ? (
                        <button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300" onClick={() => toggleLoginPopup(true)}>Sign in</button>
                      ) : (
                        <Link
                          to="/login"
                          className="block w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                          onClick={toggleMobileMenu}
                        >
                          Sign in
                        </Link>
                      )}
                      
                    </div>
                  )}
                </nav>
              </div>
            </div>
          )}

          <main className="container mx-auto animate-fadeIn pb-16 sm:pb-0">
            {!isMobile && (
              <section className="relative h-[420px] bg-black overflow-hidden">
                <Swiper
                  modules={[Autoplay, Pagination]}
                  autoplay={{ delay: 4000, disableOnInteraction: false }}
                  pagination={{ clickable: true }}
                  loop={true}
                  speed={1000}
                  className="h-full desktop-hero-swiper"
                >
                  {[
                    {
                      url: "https://cdn.pixabay.com/photo/2024/04/26/18/36/farm-house-8722429_1280.jpg",
                      text: "Seamlessly Connect with Verified Hosts"
                    },
                    {
                      url: "https://cdn.pixabay.com/photo/2017/09/05/11/03/castle-2717222_1280.jpg",
                      text: "From Cozy Budget Homes to Lavish Retreats"
                    },
                    {
                      url: "https://cdn.pixabay.com/photo/2017/08/27/10/16/interior-2685521_1280.jpg",
                      text: "We Curate Every Stay with Care"
                    }
                  ].map((slide, idx) => (
                    <SwiperSlide key={idx}>
                      <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url('${slide.url}')` }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-transparent"></div>
                      <div className="absolute inset-0 flex flex-col justify-center items-center px-8">
                        <p className="text-[30px] text-white text-center font-semibold drop-shadow">{slide.text}</p>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div className="absolute left-0 right-0 bottom-0 z-20 px-12 pb-8">
                  <div className="flex items-center gap-3 text-white mb-3">
                    <MapPin className="h-6 w-6" />
                    <div className="flex-1">
                      <div className="text-base opacity-60">Current Location</div>
                      <div className="font-medium font-semibold flex items-center gap-2">
                        {userCity}
                        {userState && <>, {userState}</>}
                        <button
                          onClick={handleLocationRefresh}
                          disabled={isRefreshingLocation}
                          className="p-1 hover:bg-white/10 rounded-full transition-colors"
                        >
                          <RotateCcw className={`h-5 w-5 ${isRefreshingLocation ? "animate-spin" : ""}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`relative transition-all duration-300 ${
                      isSearchTransitioning ? "-translate-y-1 scale-[1.02] opacity-90" : ""
                    }`}
                  >
                    <input
                      type="text"
                      placeholder={placeholderText}
                      value={aiSearchQuery}
                      onChange={(e) => setAiSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAiSearch();
                        }
                      }}
                      className="w-full py-4 px-6 pr-16 rounded-full bg-white text-gray-900 placeholder-gray-500 focus:outline-none text-base"
                    />
                    <button
                      className="absolute right-5 top-1/2 -translate-y-1/2"
                      onClick={handleAiSearch}
                      disabled={isSearching || isSearchTransitioning}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </section>
            )}

            <FilterMenu
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              onApply={(filters) => {
                applyFilters(filters)
                if (filters.sortBy !== sortBy) {
                  setSortBy(filters.sortBy)
                  handleSort(filters.sortBy)
                }
              }}
              sortBy={sortBy}
            />
            {/* Mobile sections for desktop view */}
            <section className="container mx-auto px-8 lg:px-16 py-8">
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold">Destinations</h2>
                  {isAdmin && (
                    <Link to="/admin/destinations" className="text-red-500 text-sm font-medium">
                      Manage
                    </Link>
                  )}
                </div>
                {destinations.length > 0 ? (
                  <div className="grid grid-cols-6 gap-4">
                    {destinations
                      .filter(dest => dest.isVisible)
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((destination) => (
                        <Link
                          key={destination.id}
                          to={`/villas-in-${destination.name.toLowerCase()}`}
                          className="flex flex-col items-center group"
                        >
                          <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-gray-200 transition-transform group-hover:scale-105">
                            <OptimizedImage
                              src={destination.imageUrl || "/placeholder.jpg"}
                              alt={`Villas in ${destination.name}`}
                              className="w-full h-full"
                              preset="avatar"
                            />
                          </div>
                          <span className="mt-3 text-lg font-medium text-gray-900">
                            {destination.name}
                          </span>
                          {destination.propertyCount > 0 && (
                            <span className="text-sm text-gray-500">
                              {destination.propertyCount} properties
                            </span>
                          )}
                        </Link>
                      ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-6 gap-4">
                    {[...Array(6)].map((_, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div className="w-32 h-32 rounded-full bg-gray-200 animate-pulse" />
                        <div className="mt-3 h-4 w-20 bg-gray-200 rounded animate-pulse" />
                        <div className="mt-2 h-3 w-16 bg-gray-200 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Value Stays Section */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold">Value Stays</h2>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div
                    onClick={() => handleValueStaysClick(1000)}
                    className="relative rounded-2xl overflow-hidden h-[280px] cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    <img
                      src="https://cdn.pixabay.com/photo/2020/10/18/09/16/bedroom-5664221_1280.jpg"
                      alt="Hotel room"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 p-4 text-white">
                      <h3 className="text-2xl font-bold mb-2">Stays</h3>
                      <div className="bg-red-400 text-white px-2 py-1 rounded-full inline-block text-lg">
                        Under ₹999
                      </div>
                    </div>
                  </div>
                  <div
                    onClick={() => handleValueStaysClick(2000)}
                    className="relative rounded-2xl overflow-hidden h-[280px] cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    <img
                      src="https://cdn.pixabay.com/photo/2019/08/19/13/58/bed-4416515_1280.jpg"
                      alt="Hostel room"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 p-4 text-white">
                      <h3 className="text-2xl font-bold mb-1">Stays</h3>
                      <div className="bg-red-400 text-white px-3 py-1 rounded-full inline-block">Under ₹1999</div>
                    </div>
                  </div>
                  <div
                    onClick={() => handleValueStaysClick(3000)}
                    className="relative rounded-2xl overflow-hidden h-[280px] cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    <img
                      src="https://cdn.pixabay.com/photo/2016/11/18/22/21/architecture-1837150_1280.jpg"
                      alt="Homestay"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 p-4 text-white">
                      <h3 className="text-2xl font-bold mb-1">Stays</h3>
                      <div className="bg-red-400 text-white px-3 py-1 rounded-full inline-block">Under ₹2999</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-6 w-full">
                <AdCard
                  adSlot="7217956528"
                  className="w-full h-[60px] sm:h-[80px] md:h/[100px] lg:h/[120px] mx-auto rounded-xl transition-all duration-300"
                />
              </div>

              {/* Recently Viewed - Desktop */}
              {recentProperties.length > 0 && (
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold">Recently Viewed Properties</h2>
                  </div>
                  <div className="relative">
                    <div className="overflow-hidden">
                      <div
                        className="flex gap-4 transition-transform duration-300 ease-in-out"
                        style={{ transform: `translateX(-${(recentSlideIndex * 100) / villasPerSlide}%)` }}
                      >
                            {recentPropertiesForDisplay.map((property, index) => (
                      <Link
                        to={`/villa/${property._id}`}
                        key={`${property._id}-${index}`}
                        className="bg-gray-50 rounded-[20px] overflow-hidden border-2 shadow border-gray-200"
                        style={{ width: "calc((100% - 3rem) / 4)", flexShrink: 0 }}
                        onClick={() => addToRecentlyViewed(property)}
                      >
                        <div className="relative p-2">
                          <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                            Recent
                          </div>
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              handleWishlistClick(property._id)
                            }}
                            className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                          >
                            {isPropertyWishlisted(property._id) ? (
                              <Heart className="h-5 w-5 text-red-500 fill-current" />
                            ) : (
                              <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                            )}
                          </button>
                          <div className="rounded-[10px] overflow-hidden h-[190px]">
                            <OptimizedImage
                              src={property.thumbnail || property.images?.[0] || "/placeholder.svg"}
                              alt={property.name}
                              className="w-full h-full"
                              preset="cardLarge"
                            />
                          </div>
                        </div>
                        <div className="px-3 pb-3 pt-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="text-base font-semibold text-gray-900 mr-1 line-clamp-2 leading-tight">{property.name}</h3>
                            <p className="text-base font-bold whitespace-nowrap leading-tight">
                              From ₹{property.rate}
                              <span className="text-xs font-normal text-gray-600"> </span>
                            </p>
                          </div>
                          <div className="mt-0.5 flex items-center text-gray-600">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="ml-1 text-xs truncate">{property.mainCity}</span>
                          </div>
                          <div className="mt-0.5 flex items-center justify-between text-xs">
                            <div className="flex items-center text-gray-600 min-w-0">
                              <Navigation className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                              {userLocation && property.location ? (
                                <span className="truncate leading-tight">
                                  {Math.round(
                                    calculateDistance(
                                      userLocation.lat,
                                      userLocation.lng,
                                      property.location.lat,
                                      property.location.lng,
                                    ) * 10,
                                  ) / 10}{" "}
                                  km away
                                </span>
                              ) : (
                                <span>Distance N/A</span>
                              )}
                            </div>
                            <div className="flex items-center ml-2 flex-shrink-0">
                              <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                              <span className="ml-1 font-medium text-xs">
                                {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                              </span>
                              <span className="ml-1 text-gray-600 text-xs">({property.reviewCount || 0})</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                              ))}
                      </div>
                    </div>
                    {recentPropertiesForDisplay.length > villasPerSlide && (
                      <>
                        <button
                          onClick={() => setRecentSlideIndex((prev) => Math.max(prev - 1, 0))}
                          disabled={recentSlideIndex === 0}
                          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md border border-gray-200 disabled:opacity-40"
                          aria-label="Previous recently viewed properties"
                        >
                          <ChevronLeftIcon className="h-5 w-5 text-gray-700" />
                        </button>
                        <button
                          onClick={() => setRecentSlideIndex((prev) => Math.min(prev + 1, recentDesktopMaxIndex))}
                          disabled={recentSlideIndex >= recentDesktopMaxIndex}
                          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md border border-gray-200 disabled:opacity-40"
                          aria-label="Next recently viewed properties"
                        >
                          <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
              {/* Marquee News Bar - Desktop */}
              <div className="marquee-container">
                <span className="marquee-text">
                  Note: Pricing varies for each Stay. Some properties follow a per-person rate, while others have fixed rates for the entire stay. Additionally, some stays offer different packages with or without food. Please check individual listings for exact pricing and inclusions
                </span>
              </div>
              {/* Popular Villas Section */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-4 ">
                  <h2 className="text-2xl font-semibold">Popular Villa's</h2>
                </div>
                <div className="relative">
                  <div className="overflow-hidden">
                    <div
                      className="flex gap-4 transition-transform duration-300 ease-in-out"
                      style={{ transform: `translateX(-${(popularSlideIndex * 100) / villasPerSlide}%)` }}
                    >
                      {popularVillasForDisplay.map((property) => (
                      <Link
                        to={`/villa/${property._id}`}
                        key={property._id}
                        className="bg-gray-50 rounded-[20px] overflow-hidden border-2 shadow border-gray-200 "
                        style={{ width: "calc((100% - 3rem) / 4)", flexShrink: 0 }}
                      >
                        <div className="relative p-2">
                          {promotedVillas.some((pv) => pv._id === property._id) ? (
                            <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-purple-600 to-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                              Featured
                            </div>
                          ) : property.averageRating >= 4 ? (
                            <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                              Popular
                            </div>
                          ) : null}
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              handleWishlistClick(property._id)
                            }}
                            className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                          >
                            {isPropertyWishlisted(property._id) ? (
                              <Heart className="h-5 w-5 text-red-500 fill-current" />
                            ) : (
                              <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                            )}
                          </button>
                          <div className="rounded-[10px] overflow-hidden h-[205px]">
                            <div className="grid grid-cols-2 gap-1 h-[205px]">
                              <div className="relative h-[205px]">
                                <LazyImage
                                  src={property.thumbnail || property.images[0]}
                                  alt={property.name}
                                  className="w-full h-full object-cover rounded-l-[20px]"
                                />
                              </div>
                              <div className="grid grid-rows-2 gap-1 h-[205px]">
                                <div className="h-[102px]">
                                  <LazyImage
                                    src={property.images[1] || "/placeholder.svg"}
                                    alt={property.name}
                                    className="w-full h-full object-cover rounded-tr-[15px]"
                                  />
                                </div>
                                <div className="h-[102px]">
                                  <LazyImage
                                    src={property.images[2] || "/placeholder.svg"}
                                    alt={property.name}
                                    className="w-full h-full object-cover rounded-br-[15px]"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="px-3 pb-3 pt-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="text-base font-semibold text-gray-900 mr-1 line-clamp-2 leading-tight">{property.name}</h3>
                            <p className="text-base font-bold whitespace-nowrap leading-tight">
                              From ₹{property.rate}
                              <span className="text-xs font-normal text-gray-600"> </span>
                            </p>
                          </div>
                          <div className="mt-0.5 flex items-center text-gray-600">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="ml-1 text-xs truncate">{property.mainCity}</span>
                          </div>
                          <div className="mt-0.5 flex items-center justify-between text-xs">
                            <div className="flex items-center text-gray-600 min-w-0">
                              <Navigation className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                              {userLocation && property.location ? (
                                <span className="truncate leading-tight">
                                  {Math.round(
                                    calculateDistance(
                                      userLocation.lat,
                                      userLocation.lng,
                                      property.location.lat,
                                      property.location.lng,
                                    ) * 10,
                                  ) / 10}{" "}
                                  km away
                                </span>
                              ) : (
                                <span>Distance N/A</span>
                              )}
                            </div>
                            <div className="flex items-center ml-2 flex-shrink-0">
                              <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                              <span className="ml-1 font-medium text-xs">
                                {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                              </span>
                              <span className="ml-1 text-gray-600 text-xs">({property.reviewCount || 0})</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                            ))}
                      <button
                        onClick={handleViewAllTopRated}
                        className="bg-white rounded-[20px] border-2 border-dashed border-red-200 flex items-center justify-center text-red-500 font-semibold hover:bg-red-50 transition-colors"
                        style={{ width: "calc((100% - 3rem) / 4)", flexShrink: 0 }}
                      >
                        Explore More
                      </button>
                    </div>
                  </div>
                  {popularVillasForDisplay.length + 1 > villasPerSlide && (
                    <>
                      <button
                        onClick={() => setPopularSlideIndex((prev) => Math.max(prev - 1, 0))}
                        disabled={popularSlideIndex === 0}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md border border-gray-200 disabled:opacity-40"
                        aria-label="Previous popular villas"
                      >
                        <ChevronLeftIcon className="h-5 w-5 text-gray-700" />
                      </button>
                      <button
                        onClick={() => setPopularSlideIndex((prev) => Math.min(prev + 1, popularDesktopMaxIndex))}
                        disabled={popularSlideIndex >= popularDesktopMaxIndex}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md border border-gray-200 disabled:opacity-40"
                        aria-label="Next popular villas"
                      >
                        <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="mb-6 w-full">
                <AdCard
                  adSlot="5292128191"
                  className="w-full h-[60px] sm:h-[80px] md:h/[100px] lg:h/[120px] mx-auto rounded-xl transition-all duration-300"
                />
              </div>
              {/* Villas Near You Section */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold">Properties Near You</h2>
                </div>
                <div className="relative">
                  <div className="overflow-hidden">
                    <div
                      className="flex gap-4 transition-transform duration-300 ease-in-out"
                      style={{ transform: `translateX(-${(nearbySlideIndex * 100) / villasPerSlide}%)` }}
                    >
                      {nearbyPropertiesForDisplay.map((property) => (
                      <Link
                        to={`/villa/${property._id}`}
                        key={property._id}
                        className="bg-white rounded-[20px] overflow-hidden relative h-[280px]"
                        style={{ width: "calc((100% - 3rem) / 4)", flexShrink: 0 }}
                      >
                        <div className="relative w-full h-full">
                          <OptimizedImage
                            src={property.thumbnail || property.images[0]}
                            alt={property.name}
                            className="w-full h-full"
                            preset="cardMedium"
                          />
                          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/70 to-transparent" />
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              handleWishlistClick(property._id)
                            }}
                            className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                          >
                            {isPropertyWishlisted(property._id) ? (
                              <Heart className="h-5 w-5 text-red-500 fill-current" />
                            ) : (
                              <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                            )}
                          </button>

                          <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                            <h3 className="text-base font-bold line-clamp-2 mb-2">{property.name}</h3>
                            <div className="flex items-center opacity-100 mb-2">
                              {userLocation && property.location ? (
                                <span className="text-sm truncate">
                                  {Math.round(
                                    calculateDistance(
                                      userLocation.lat,
                                      userLocation.lng,
                                      property.location.lat,
                                      property.location.lng,
                                    ) * 10,
                                  ) / 10}{" "}
                                  km away
                                </span>
                              ) : (
                                <span className="text-sm">Distance N/A</span>
                              )}
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-base font-bold whitespace-nowrap">From ₹{property.rate}</p>
                              <div className="flex items-center ml-2 flex-shrink-0">
                                <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                                <span className="ml-1">
                                  {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                      <button
                        onClick={handleViewAllNearby}
                        className="bg-white rounded-[20px] border-2 border-dashed border-red-200 flex items-center justify-center text-red-500 font-semibold hover:bg-red-50 transition-colors h-[280px]"
                        style={{ width: "calc((100% - 3rem) / 4)", flexShrink: 0 }}
                      >
                        Explore More
                      </button>
                    </div>
                  </div>
                  {nearbyPropertiesForDisplay.length + 1 > villasPerSlide && (
                    <>
                      <button
                        onClick={() => setNearbySlideIndex((prev) => Math.max(prev - 1, 0))}
                        disabled={nearbySlideIndex === 0}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md border border-gray-200 disabled:opacity-40"
                        aria-label="Previous nearby properties"
                      >
                        <ChevronLeftIcon className="h-5 w-5 text-gray-700" />
                      </button>
                      <button
                        onClick={() => setNearbySlideIndex((prev) => Math.min(prev + 1, nearbyDesktopMaxIndex))}
                        disabled={nearbySlideIndex >= nearbyDesktopMaxIndex}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md border border-gray-200 disabled:opacity-40"
                        aria-label="Next nearby properties"
                      >
                        <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="mb-6 w-full">
                <AdCard
                  adSlot="6596506975"
                  className="w-full h-[60px] sm:h-[80px] md:h/[100px] lg:h/[120px] mx-auto rounded-xl transition-all duration-300"
                />
              </div>
              {/* Special Offers Section */}
              {hasAnyOffers && (
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold">Offers & Discount</h2>
                  <Link to="/offers" className="text-red-500 text-sm font-medium hover:text-red-600">
                    View all
                    <ChevronRightIcon className="h-4 w-4 ml-1 inline" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {featuredOffers.length > 0
                    ? featuredOffers.slice(0, 2).map((property) => (
                      <Link
                        to={`/villa/${property._id}`}
                        key={property._id}
                        className="bg-white rounded-[20px] overflow-hidden relative h-[280px]"
                      >
                        <div className="relative w-full h-full">
                          <OptimizedImage
                            src={property.thumbnail || property.images[0]}
                            alt={property.name}
                            preset="cardMedium"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/70 to-transparent" />
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              handleWishlistClick(property._id)
                            }}
                            className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                          >
                            {isPropertyWishlisted(property._id) ? (
                              <Heart className="h-5 w-5 text-red-500 fill-current" />
                            ) : (
                              <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                            )}
                          </button>
                          <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                            <h3 className="text-xl font-bold line-clamp-2 mb-2">
                              {property.offerMessage ||
                                `${property.name} offers you ${property.discount}% discount on Weekdays`}
                            </h3>
                            <div className="mt-1">
                              <p className="text-sm line-through text-gray-300">₹{property.rate}</p>
                              <p className="text-lg font-bold">
                                From ₹{Math.round(property.rate * (1 - property.discount / 100))}
                              </p>
                            </div>
                            <div className="mt-2 flex justify-between items-center">
                              <button
                                onClick={(e) => {
                                  e.preventDefault()
                                  navigate(`/villa/${property._id}`)
                                }}
                                className="bg-red-500 text-white px-4 py-2 rounded-full text-sm hover:bg-red-600 transition-colors"
                              >
                                Book Now
                              </button>
                              <span className="text-xs text-gray-300">*Terms & Conditions</span>
                            </div>
                            <div className="mt-1 flex justify-between items-center">
                              <span className="text-sm">
                                Offer valid till{" "}
                                {new Date(
                                  property.offerValidTill || Date.now() + 7 * 24 * 60 * 60 * 1000,
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))
                    : promotedVillas
                      .filter(
                        (property) =>
                          property.discount &&
                          property.discount > 0 &&
                          new Date(property.offerValidTill || Date.now()) > new Date(),
                      )
                      .slice(0, 2)
                      .map((property) => (
                        <Link
                          to={`/villa/${property._id}`}
                          key={property._id}
                          className="bg-white rounded-[20px] overflow-hidden relative h-[280px]"
                        >
                          <div className="relative w-full h-full">
                            <OptimizedImage
                              src={property.thumbnail || property.images[0]}
                              alt={property.name}
                              preset="cardMedium"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/70 to-transparent" />
                            <button
                              onClick={(e) => {
                                e.preventDefault()
                                handleWishlistClick(property._id)
                              }}
                              className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                            >
                              {isPropertyWishlisted(property._id) ? (
                                <Heart className="h-5 w-5 text-red-500 fill-current" />
                              ) : (
                                <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                              )}
                            </button>
                            <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                              <h3 className="text-xl font-bold line-clamp-2 mb-2">
                                {property.offerMessage ||
                                  `${property.name} offers you ${property.discount}% discount on Weekdays`}
                              </h3>
                              <div className="mt-1">
                                <p className="text-sm line-through text-gray-300">₹{property.rate}</p>
                                <p className="text-lg font-bold">
                                  From ₹{Math.round(property.rate * (1 - property.discount / 100))}
                                </p>
                              </div>
                              <div className="mt-2 flex justify-between items-center">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault()
                                    navigate(`/villa/${property._id}`)
                                  }}
                                  className="bg-red-500 text-white px-4 py-2 rounded-full text-sm hover:bg-red-600 transition-colors"
                                >
                                  Book Now
                                </button>
                                <span className="text-xs text-gray-300">*Terms & Conditions</span>
                              </div>
                              <div className="mt-1 flex justify-between items-center">
                                <span className="text-sm">
                                  Offer valid till{" "}
                                  {new Date(
                                    property.offerValidTill || Date.now() + 7 * 24 * 60 * 60 * 1000,
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                </div>
              </div>
              )}
              <div className="mb-6 w-full">
                <AdCard
                  adSlot="1240964864"
                  className="w-full h-[60px] sm:h/[80px] md:h/[100px] lg:h/[120px] mx-auto rounded-xl transition-all duration-300"
                />
              </div>
              {/* Luxury Villa Section */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold">Luxury Home's</h2>
                </div>
                <div className="relative">
                  <div className="overflow-hidden">
                    <div
                      className="flex gap-4 transition-transform duration-300 ease-in-out"
                      style={{ transform: `translateX(-${(luxurySlideIndex * 100) / villasPerSlide}%)` }}
                    >
                      {luxuryVillasForDisplay.map((property) => (
                    <Link
                      to={`/villa/${property._id}`}
                      key={property._id}
                      className="bg-gray-50 rounded-[20px] overflow-hidden border-2 shadow border-gray-200"
                      style={{ width: "calc((100% - 3rem) / 4)", flexShrink: 0 }}
                    >
                      <div className="relative p-2">
                        <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-yellow-600 to-amber-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                          Luxury
                        </div>
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            handleWishlistClick(property._id)
                          }}
                          className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                        >
                          {isPropertyWishlisted(property._id) ? (
                            <Heart className="h-5 w-5 text-red-500 fill-current" />
                          ) : (
                            <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                          )}
                        </button>
                        <div className="rounded-[10px] overflow-hidden h-[190px] luxury-card-swiper">
                          <Swiper
                            modules={[Pagination]}
                            spaceBetween={0}
                            slidesPerView={1}
                            navigation={false}
                            pagination={{ clickable: true }}
                            className="h-full w-full"
                          >
                            {[property.thumbnail || property.images[0], ...(property.images || []).filter(Boolean)]
                              .slice(0, 4)
                              .map((image, index) => (
                                <SwiperSlide key={index}>
                                  <OptimizedImage
                                    src={image || "/placeholder.svg"}
                                    alt={`${property.name} - Image ${index + 1}`}
                                    className="w-full h-full"
                                    preset="cardLarge"
                                  />
                                </SwiperSlide>
                              ))}
                          </Swiper>
                        </div>
                      </div>

                      <div className="px-3 pb-3 pt-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-base font-semibold text-gray-900 mr-1 line-clamp-2 leading-tight">{property.name}</h3>
                          <p className="text-base font-bold whitespace-nowrap leading-tight">
                            From ₹{property.rate}
                            <span className="text-xs font-normal text-gray-600"> </span>
                          </p>
                        </div>
                        <div className="mt-0.5 flex items-center text-gray-600">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="ml-1 text-xs truncate">{property.mainCity}</span>
                        </div>
                        <div className="mt-0.5 flex items-center justify-between text-xs">
                          <div className="flex items-center text-gray-600 min-w-0">
                            <Navigation className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                            {userLocation && property.location ? (
                              <span className="truncate leading-tight">
                                {Math.round(
                                  calculateDistance(
                                    userLocation.lat,
                                    userLocation.lng,
                                    property.location.lat,
                                    property.location.lng,
                                  ) * 10,
                                ) / 10}{" "}
                                km away
                              </span>
                            ) : (
                              <span>Distance N/A</span>
                            )}
                          </div>
                          <div className="flex items-center ml-2 flex-shrink-0">
                            <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                            <span className="ml-1 font-medium text-xs">
                              {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                            </span>
                            <span className="ml-1 text-gray-600 text-xs">({property.reviewCount || 0})</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                      <button
                        onClick={() => navigate("/explore", { state: { filterType: "luxury" } })}
                        className="bg-white rounded-[20px] border-2 border-dashed border-red-200 flex items-center justify-center text-red-500 font-semibold hover:bg-red-50 transition-colors"
                        style={{ width: "calc((100% - 3rem) / 4)", flexShrink: 0 }}
                      >
                        Explore More
                      </button>
                    </div>
                  </div>
                  {luxuryVillasForDisplay.length + 1 > villasPerSlide && (
                    <>
                      <button
                        onClick={() => setLuxurySlideIndex((prev) => Math.max(prev - 1, 0))}
                        disabled={luxurySlideIndex === 0}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 luxury-nav-btn disabled:opacity-40"
                        aria-label="Previous luxury villas"
                      >
                        <ChevronLeftIcon className="h-5 w-5 text-gray-700" />
                      </button>
                      <button
                        onClick={() => setLuxurySlideIndex((prev) => Math.min(prev + 1, luxuryDesktopMaxIndex))}
                        disabled={luxurySlideIndex >= luxuryDesktopMaxIndex}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 luxury-nav-btn disabled:opacity-40"
                        aria-label="Next luxury villas"
                      >
                        <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="mb-6 w-full">
                <AdCard
                  adSlot="4947016404"
                  className="w-full h-[60px] sm:h/[80px] md:h/[100px] lg:h/[120px] mx-auto rounded-xl transition-all duration-300"
                />
              </div>
              {/* Special Occasions Section */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-semibold">Celebrate Your Special Occasion</h2>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  {occasions
                    .filter((occasion) => occasion.isVisible)
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .slice(0, 4)
                    .map((occasion) => (
                      <Link
                        key={occasion.id}
                        to="/explore"
                        state={{
                          filterType: "occasion",
                          facilities: occasion.facilities,
                          occasionTitle: occasion.title,
                        }}
                        className="relative rounded-xl overflow-hidden h-[250px] cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        <OptimizedImage
                          src={occasion.imageUrl || "/placeholder.svg"}
                          alt={occasion.title}
                          preset="cardMedium"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 p-3 text-white">
                          <h3 className="text-2xl md:text-3xl font-bold leading-[1.1] tracking-tight line-clamp-2">{occasion.title}</h3>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
              {/* List Your Home Section - Desktop */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-xl font-semibold">For Property Owners</h2>
                </div>
                <Link to="/for-owner" className="block">
                  <div className="relative rounded-xl overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                    <div className="flex items-center gap-6">
                      <img
                        src="https://cdn.pixabay.com/photo/2016/11/18/17/46/house-1836070_1280.jpg"
                        alt="List your property"
                        className="w-48 h-48 object-cover rounded-md shadow flex-shrink-0"
                      />
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-2">
                          List Your Property on VillaKosh
                        </h3>
                        <p className="text-sm text-white/90 mb-3">
                          Direct WhatsApp inquiries from guests – no middleman. Promote your villa to thousands of travelers across Maharashtra.
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Free Listing (Limited-Time)</span>
                          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Direct Contact</span>
                          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Verified Guests</span>
                        </div>
                        <button className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-2 rounded-full text-base font-medium transition-colors">
                          List Now
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
              <div className="mb-6 w-full">
                <AdCard
                  adSlot="1240964864"
                  className="w-full h-[180px] sm:h/[200px] md:h/[220px] lg:h/[248px] mx-auto rounded-xl transition-all duration-300"
                />
              </div>
            </section>
          </main>
        </>
      )}

      {/* AI bot temporarily disabled
      <button
        onClick={() => setShowBot(true)}
        className="fixed z-[100] bottom-20 right-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-all w-16 h-16 sm:bottom-8 sm:w-20 sm:h-20"
        style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}
        aria-label="Open Bot"
      >
        <img
          src="/bot-icon.png"
          alt="Open Bot"
          className="w-full h-full object-cover rounded-full"
        />
      </button>
      {showBot && (
        <div className="fixed inset-0 z-[9999] flex items-end justify-end">
          <div className="w-full h-full">
            <BotpressChat />
          </div>
          <button
            onClick={() => setShowBot(false)}
            className="absolute top-4 right-4 z-[10000] bg-white text-black rounded-full p-2 shadow"
            aria-label="Close Bot"
          >
            ✕
          </button>
        </div>
      )}
      */}

      {isMobile && <MobileBottomNav user={user} className="h-16" />}
      {!isMobile && <Footer />}

      {showLoginPopup && <LoginPopup onClose={() => toggleLoginPopup(false)} />}
      {showLogoutConfirmation && (
        <LogoutConfirmationPopup onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
      )}
      {(isBlurred || showMobileMenu) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"></div>
      )}
    </div>
  )
}
